import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  canAdjustBills,
  canForceClose,
  canOpenSessions,
  canUseCounter,
  roleHome,
} from '@/auth/roles';
import { useStaffSession } from '@/auth/react';
import { Badge } from '@/components/Badge';
import { Button, buttonClasses } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Money } from '@/components/Money';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { useRealtimeState } from '@/realtime/react';
import { formatParty, isLocationType } from '@/venue/venue';
import type { FloorLocation, FloorSession } from './api';
import {
  Amounts,
  AdjustmentList,
  asMoney,
  BillLines,
  BillNotes,
  Notice,
  PaymentList,
  SettlementBadge,
} from './parts';
import { AdjustmentSheet, CloseSheet, OpenSessionSheet, PaymentSheet } from './sheets';
import {
  isNoBillYet,
  useAssistance,
  useBill,
  useCounterLive,
  useFloor,
  useIntentKey,
  usePrintBill,
} from './queries';
import { assistanceLabel, formatDuration, locationText, sessionStatusLabel } from './wording';
import { PageHeader } from '@/components/PageHeader';
import { SectionHeading } from '@/components/SectionHeading';
import { Icon } from '@/components/Icon';
import { cardClasses } from '@/components/Card';
import { ReadyState } from '@/components/ReadyState';
import { useToastPlacement } from '@/components/Toast';

interface Selected {
  sessionId: string;
  where: string;
  party: string;
  hasPendingOrders: boolean;
}

function partyText(location: FloorLocation, session: FloorSession): string {
  return isLocationType(location.location_type)
    ? formatParty(location.location_type, session.party_display_name, session.party_size)
    : session.party_display_name;
}

// ─── Floor ───────────────────────────────────────────────────────────────────

/**
 * One seated party as a row of the sessions panel: the place and the bill total (the backend's figure) on the
 * first line, the party and the time since opening under it, then the chips. A session whose server total is
 * zero (nothing ordered, or everything cancelled or discounted away) says so in words rather than "₹0.00".
 * The chosen row has a burgundy bar on its left edge and the burgundy tint, like the active item of a rail.
 */
function SessionButton({
  location,
  session,
  help,
  selected,
  onSelect,
}: {
  location: FloorLocation;
  session: FloorSession;
  /** Types of the session's open help requests, when known; the floor's flag alone shows a neutral chip. */
  help: string[];
  selected: boolean;
  onSelect: () => void;
}) {
  const where = locationText(location.location_type, location.location_label);
  const chips = session.has_active_assistance
    ? help.length > 0
      ? [...new Set(help)].map(assistanceLabel)
      : ['Help requested']
    : [];
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'flex min-h-14 w-full flex-col gap-1 border-l-[3px] py-2.5 pr-4 pl-3 text-left',
        selected ? 'border-primary bg-primary-soft' : 'border-transparent hover:bg-surface',
      )}
    >
      <span className="flex items-baseline justify-between gap-3">
        <span className="text-base font-bold break-words text-ink">{where}</span>
        {session.bill_total === 0 ? (
          <span className="shrink-0 text-sm font-medium text-muted">Nothing billed yet</span>
        ) : (
          <span className="shrink-0 text-base font-bold text-ink tabular-nums">
            <Money value={asMoney(session.bill_total)} />
          </span>
        )}
      </span>
      <span className="flex items-baseline justify-between gap-3 text-sm text-muted">
        <span className="min-w-0 break-words">{partyText(location, session)}</span>
        <span className="shrink-0 tabular-nums">
          {sessionStatusLabel(session.status)} · {formatDuration(session.duration_minutes)}
        </span>
      </span>
      {(session.has_pending_orders || chips.length > 0) && (
        <span className="flex flex-wrap items-center gap-1.5">
          {session.has_pending_orders && <Badge tone="active">In the kitchen</Badge>}
          {chips.map((chip) => (
            <Badge key={chip} tone="attention">
              {chip}
            </Badge>
          ))}
        </span>
      )}
    </button>
  );
}

function FloorList({
  selectedId,
  onSelect,
  onOpened,
  canOpen,
}: {
  selectedId: string | null;
  onSelect: (selected: Selected) => void;
  onOpened: (sessionId: string) => void;
  canOpen: boolean;
}) {
  const floor = useFloor();
  const live = useRealtimeState() === 'live';
  const [showVacant, setShowVacant] = useState(false);
  const [opening, setOpening] = useState<FloorLocation | null>(null);

  const locations = useMemo(() => floor.data ?? [], [floor.data]);
  const anyHelp = locations.some((l) => l.active_sessions.some((s) => s.has_active_assistance));
  const assistance = useAssistance(anyHelp);
  const helpBySession = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const request of assistance.data ?? []) {
      if (request.status !== 'PENDING') continue;
      map.set(request.session_id, [...(map.get(request.session_id) ?? []), request.type]);
    }
    return map;
  }, [assistance.data]);
  const groups = useMemo(() => {
    const shown = locations.filter((l) => l.active_sessions.length > 0 || (canOpen && showVacant));
    const byZone = new Map<string, FloorLocation[]>();
    for (const location of shown) {
      const zone = location.zone_name ?? 'Other';
      byZone.set(zone, [...(byZone.get(zone) ?? []), location]);
    }
    return [...byZone.entries()];
  }, [locations, canOpen, showVacant]);
  const hasSessions = locations.some((l) => l.active_sessions.length > 0);
  const vacant = locations.filter((l) => l.status === 'VACANT').length;

  return (
    <section aria-labelledby="sessions-title" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="sessions-title" className="text-lg font-bold text-ink">
          Sessions
        </h2>
        <div className="flex items-center gap-2">
          {canOpen && (
            <Button
              variant="secondary"
              aria-pressed={showVacant}
              className={showVacant ? 'border-primary bg-primary-soft text-primary' : undefined}
              onClick={() => setShowVacant((value) => !value)}
            >
              {showVacant && <Icon name="check" size={16} />}
              {showVacant ? 'Showing vacant' : 'Show vacant'}
            </Button>
          )}
          <Button
            variant="secondary"
            loading={floor.isFetching && !floor.isPending}
            onClick={() => void floor.refetch()}
          >
            Refresh
          </Button>
        </div>
      </div>

      {floor.isPending && (
        <LoadingRegion slowHint label="Loading sessions">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        </LoadingRegion>
      )}
      {floor.isError && !floor.data && (
        <ErrorState error={floor.error} onRetry={() => void floor.refetch()} />
      )}
      {floor.isError && floor.data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void floor.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the sessions. Showing the last list received.
        </Notice>
      )}
      {floor.data && !hasSessions && groups.length === 0 && (
        <ReadyState
          id="counter-ready-title"
          level={3}
          title="Counter is ready"
          message="Active sessions will appear here when guests start ordering."
        >
          {locations.length > 0 && (
            <dl className="flex items-baseline gap-1.5 text-sm">
              <dt className="text-muted">Floor</dt>
              <dd className="font-semibold text-ink tabular-nums">
                {vacant} of {locations.length} vacant
              </dd>
            </dl>
          )}
          {live && <Badge tone="success">Live</Badge>}
        </ReadyState>
      )}
      {groups.map(([zone, items]) => (
        <div key={zone} className="flex flex-col gap-2">
          <SectionHeading level={3}>{zone}</SectionHeading>
          <ul
            className={cn(cardClasses({ padding: 'none' }), 'divide-y divide-line overflow-hidden')}
          >
            {items.map((location) =>
              location.active_sessions.length > 0 ? (
                location.active_sessions.map((session) => (
                  <li key={session.session_id}>
                    <SessionButton
                      location={location}
                      session={session}
                      help={helpBySession.get(session.session_id) ?? []}
                      selected={selectedId === session.session_id}
                      onSelect={() =>
                        onSelect({
                          sessionId: session.session_id,
                          where: locationText(location.location_type, location.location_label),
                          party: partyText(location, session),
                          hasPendingOrders: session.has_pending_orders,
                        })
                      }
                    />
                  </li>
                ))
              ) : (
                <li
                  key={location.location_id}
                  className="flex min-h-14 items-center justify-between gap-3 bg-surface/60 py-2 pr-4 pl-[15px]"
                >
                  <span className="text-base text-muted">
                    {locationText(location.location_type, location.location_label)} · Vacant
                  </span>
                  <Button
                    variant="secondary"
                    aria-label={`Open session at ${locationText(location.location_type, location.location_label)}`}
                    onClick={() => setOpening(location)}
                  >
                    <Icon name="plus" size={16} />
                    Open session
                  </Button>
                </li>
              ),
            )}
          </ul>
        </div>
      ))}
      {opening && (
        <OpenSessionSheet
          location={opening}
          onClose={() => setOpening(null)}
          onOpened={(sessionId) => {
            setOpening(null);
            onOpened(sessionId);
          }}
        />
      )}
    </section>
  );
}

// ─── Bill ────────────────────────────────────────────────────────────────────

type Dialog = 'payment' | 'adjust' | 'close' | null;

function BillPanel({
  selected,
  stillOpen,
  onBack,
  onClosed,
  role,
}: {
  selected: Selected;
  /** `null` while the floor has not answered, so "no longer open" is never claimed early. */
  stillOpen: boolean | null;
  onBack: () => void;
  onClosed: () => void;
  role: Parameters<typeof canUseCounter>[0];
}) {
  // No bill is read for a session the floor no longer lists: there is nothing to show for it.
  const bill = useBill(selected.sessionId, stillOpen !== false);
  const print = usePrintBill();
  const printKey = useIntentKey();
  const [dialog, setDialog] = useState<Dialog>(null);
  const noBill = bill.isError && !bill.data && isNoBillYet(bill.error);
  const data = bill.data;

  function printBill() {
    if (!data || print.isPending) return;
    print.mutate(
      { billId: data.bill_id, idempotencyKey: printKey.keyFor(`print:${data.bill_id}`) },
      { onSuccess: () => printKey.reset() },
    );
  }

  if (stillOpen === false) {
    // One state for a closed or unknown session: no stale bill, no raw reference, one way back.
    return (
      <section aria-label="Bill" className="flex min-w-0 flex-col gap-4">
        <div className={cn(cardClasses(), 'flex min-h-96 items-center justify-center')}>
          <EmptyState
            icon="alert"
            title="This session isn't open"
            message="It may have been closed, or the link is out of date."
            action={
              <Button variant="secondary" onClick={onBack}>
                Back to sessions
              </Button>
            }
          />
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="bill-title" className="flex min-w-0 flex-col gap-4">
      <Button
        variant="secondary"
        onClick={onBack}
        aria-label="Back to sessions"
        className="self-start lg:hidden"
      >
        <Icon name="chevronLeft" size={18} />
        Sessions
      </Button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="bill-title" className="text-2xl leading-8 font-bold break-words text-ink">
            {selected.where}
          </h2>
          <p className="text-sm break-words text-muted">{selected.party}</p>
        </div>
        {data && <SettlementBadge status={data.settlement_status} />}
      </div>

      {bill.isPending && (
        <LoadingRegion slowHint label="Loading bill">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24" />
            <Skeleton className="h-32" />
            <Skeleton className="h-24" />
          </div>
        </LoadingRegion>
      )}

      {noBill && (
        <>
          <EmptyState
            icon="bill"
            title="No bill yet"
            message="Nothing has been ordered in this session."
            action={
              <Button variant="primary" size="lg" onClick={() => setDialog('close')}>
                Close session
              </Button>
            }
          />
        </>
      )}

      {bill.isError && !data && !noBill && (
        <ErrorState error={bill.error} onRetry={() => void bill.refetch()} />
      )}

      {bill.isError && data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void bill.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the bill. The amounts below may be out of date.
        </Notice>
      )}

      {data && (
        <>
          <BillNotes bill={data} hasPendingOrders={selected.hasPendingOrders} />
          <Amounts bill={data} />
          <div
            role="group"
            aria-label="Bill actions"
            className="flex flex-wrap items-center gap-2 border-y border-line py-3"
          >
            {data.remaining_balance !== 0 && (
              <Button variant="primary" size="lg" onClick={() => setDialog('payment')}>
                Record payment
              </Button>
            )}
            <Button variant="secondary" size="lg" loading={print.isPending} onClick={printBill}>
              Print bill
            </Button>
            {canAdjustBills(role) && (
              <Button variant="secondary" size="lg" onClick={() => setDialog('adjust')}>
                Adjust bill
              </Button>
            )}
            <Button variant="secondary" size="lg" onClick={() => setDialog('close')}>
              Close session
            </Button>
          </div>
          <section aria-labelledby="items-title" className="flex flex-col gap-2">
            <SectionHeading id="items-title" level={3}>
              Items
            </SectionHeading>
            <BillLines bill={data} />
          </section>
          <AdjustmentList adjustments={data.adjustments} />
          <PaymentList payments={data.payments} />
        </>
      )}

      {dialog === 'payment' && data && <PaymentSheet bill={data} onClose={() => setDialog(null)} />}
      {dialog === 'adjust' && data && (
        <AdjustmentSheet bill={data} onClose={() => setDialog(null)} />
      )}
      {dialog === 'close' && (
        <CloseSheet
          sessionId={selected.sessionId}
          where={`${selected.where} · ${selected.party}`}
          bill={data ?? null}
          canForce={canForceClose(role)}
          onClose={() => setDialog(null)}
          onClosed={() => {
            setDialog(null);
            onClosed();
          }}
        />
      )}
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function CounterBoard({ role }: { role: Parameters<typeof canUseCounter>[0] }) {
  useCounterLive();
  // Toasts (print results, closes) sit at the top so they never cover the bill actions or the figures.
  useToastPlacement('top');
  const [params, setParams] = useSearchParams();
  const floor = useFloor();
  const sessionId = params.get('session');
  const [remembered, setRemembered] = useState<Selected | null>(null);

  const found = useMemo(() => {
    if (!sessionId) return null;
    for (const location of floor.data ?? []) {
      const session = location.active_sessions.find((s) => s.session_id === sessionId);
      if (session) {
        return {
          sessionId,
          where: locationText(location.location_type, location.location_label),
          party: partyText(location, session),
          hasPendingOrders: session.has_pending_orders,
        } satisfies Selected;
      }
    }
    return null;
  }, [floor.data, sessionId]);

  const selected =
    found ??
    (remembered && remembered.sessionId === sessionId ? remembered : null) ??
    (sessionId ? { sessionId, where: 'Session', party: '', hasPendingOrders: false } : null);
  const stillOpen = floor.data ? found !== null : null;

  const select = (next: Selected | null) => {
    setRemembered(next);
    setParams(next ? { session: next.sessionId } : {}, { replace: false });
  };

  const hasSessions = (floor.data ?? []).some((l) => l.active_sessions.length > 0);
  /** One column while the floor is known to be empty and nothing is open: there is no bill pane to fill. */
  const readyOnly = selected === null && floor.data !== undefined && !hasSessions;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
      <PageHeader title="Counter" />
      {/* The same first child in both layouts, so FloorList keeps its Show vacant state across the switch. */}
      <div
        className={
          readyOnly
            ? 'w-full max-w-3xl'
            : 'grid items-start gap-6 lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]'
        }
      >
        <div className={cn(selected ? 'hidden lg:block' : 'block')}>
          <FloorList
            selectedId={sessionId}
            onSelect={select}
            canOpen={canOpenSessions(role)}
            onOpened={(id) => setParams({ session: id })}
          />
        </div>
        {!readyOnly && (
          <div className={cn(selected ? 'block' : 'hidden lg:block')}>
            {selected ? (
              <BillPanel
                key={selected.sessionId}
                selected={selected}
                stillOpen={stillOpen}
                role={role}
                onBack={() => select(null)}
                onClosed={() => select(null)}
              />
            ) : (
              <div className={cn(cardClasses(), 'flex min-h-96 items-center justify-center')}>
                <EmptyState
                  icon="bill"
                  title="Select a session"
                  message="Choose a table or seat to see its bill."
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function CounterPage() {
  const session = useStaffSession();
  const role = session?.user.role;
  if (!role) return null;
  if (!canUseCounter(role)) {
    return (
      <EmptyState
        icon="alert"
        title="Not allowed"
        message="You don't have access to the counter."
        action={
          <Link to={roleHome(role)} className={buttonClasses({ variant: 'secondary' })}>
            Go to your home screen
          </Link>
        }
      />
    );
  }
  return <CounterBoard role={role} />;
}
